import {
  Children,
  ComponentType,
  Dispatch,
  LegacyRef,
  MutableRefObject,
  ReactNode,
  RefCallback,
  SetStateAction,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { dequal } from 'dequal';

import { useEvent } from '@hooks/use-event';
import { Timeout } from '@utils/types';

import { getVisibleHeight, getVisibleWidth, debounce } from '../utils/browser';

let id = 0;

function genId() {
  return ++id;
}

export function useAriaId(prefix: string) {
  const [id] = useState(() => genId().toString());
  return `aria-${prefix}-${id}`;
}

export function mergeRefs<T = any>(
  refs: Array<MutableRefObject<T> | LegacyRef<T>>
): RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null) {
        (ref as MutableRefObject<T | null>).current = value;
      }
    });
  };
}

export function useMergedRefs<T = any>(
  refs: Array<MutableRefObject<T> | LegacyRef<T>>
): RefCallback<T> {
  return useState(() => mergeRefs(refs))[0];
}

const resolvedFonts = new Map<string, string>();

// Canvas can't parse var() theme tokens — resolve them to a concrete shorthand
// through a probe element (getComputedStyle returns px values), cached per token.
function resolveFont(font: string) {
  if (!font.startsWith('var(')) {
    return font;
  }
  let resolved = resolvedFonts.get(font);
  if (!resolved) {
    const probe = document.createElement('span');
    probe.style.font = font;
    document.body.append(probe);
    const { fontWeight, fontSize, fontFamily } = getComputedStyle(probe);
    resolved = `${fontWeight} ${fontSize} ${fontFamily}`;
    probe.remove();
    resolvedFonts.set(font, resolved);
  }
  return resolved;
}

export function calculateTextWidth(text: string, font: string) {
  const canvas: HTMLCanvasElement =
    // @ts-ignore
    calculateTextWidth.canvas || (calculateTextWidth.canvas = document.createElement('canvas'));

  const context = canvas.getContext('2d');
  context!.font = resolveFont(font);
  const metrics = context!.measureText(text);
  return metrics.width + 10;
}

export function useAccessibleLabelWarning(props: any) {
  if (process.env.NODE_ENV === 'development') {
    if (!props['aria-label'] && !props['aria-labelledby']) {
      warnWithStackTrace('Missing aria-label or aria-labelledby');
    }
  }
}

export function warnWithStackTrace(message: string) {
  const err = new Error(message);
  console.warn(err.stack);
}

export function findNextScrollableParent(node: HTMLElement | null): HTMLElement | null {
  if (node == null) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return findNextScrollableParent(node.parentNode as HTMLElement);
  }
}

export function useLocalStorageState<T>({
  key,
  defaultValue,
  valueAsNumber,
}: {
  key: string | null;
  defaultValue: T;
  valueAsNumber?: boolean;
}) {
  const [state, setState] = useState(() => {
    if (!key) return defaultValue;

    const stored = localStorage.getItem(key);
    if (!stored) return defaultValue;

    let parsed = JSON.parse(stored);
    if (valueAsNumber) parsed = Number(parsed);

    return parsed as T;
  });

  const setStorageValue = useCallback(
    (value: SetStateAction<T>) => {
      setState((prevState) => {
        const newValue =
          typeof value === 'function' ? (value as (prevState: T) => T)(prevState) : value;

        if (key) {
          localStorage.setItem(key, JSON.stringify(newValue));
        }

        return newValue;
      });
    },
    [key]
  );

  return [state, setStorageValue] as [T, (value: SetStateAction<T>) => void];
}

export function usePreviousValue<T>(value: T) {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function useDeepEqualMemoValue<T>(value: T): T {
  const ref = useRef<T | undefined>(undefined);

  if (ref.current instanceof File && value instanceof File) {
    if (ref.current.size !== value.size) {
      ref.current = value;
    }
  } else if (!dequal(ref.current, value)) {
    ref.current = value;
  }

  return ref.current as T;
}

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    height: getVisibleHeight(),
    width: getVisibleWidth(),
  });

  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowSize({
        height: getVisibleHeight(),
        width: getVisibleWidth(),
      });
    }, 300);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

export function useIsInsideModalOrSidebar(ref: MutableRefObject<HTMLDivElement | null>) {
  const [isInside, setIsInside] = useState(false);

  useLayoutEffect(() => {
    const modalElement = document.getElementById('modal');
    const sidebarElement = document.getElementById('sidebar');
    setIsInside(
      Boolean(modalElement?.contains(ref.current) || sidebarElement?.contains(ref.current))
    );
  }, [ref]);

  return isInside;
}

export function useDebouncedValue<T>(value: T, delay?: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    if (!delay) return;

    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return delay ? debouncedValue : value;
}

export function useDebouncedState<T>(initialValue: T, delay?: number) {
  const [value, setValue] = useState(initialValue);
  const debouncedValue = useDebouncedValue(value, delay);

  return [debouncedValue, setValue] as [T, Dispatch<SetStateAction<T>>];
}

export function useOverridableDebouncedState<T>(initialValue: T, delay?: number) {
  const [value, setValueInstantly] = useState(initialValue);
  const timeoutRef = useRef<Timeout>();

  const setValue = (newValue: T) => {
    if (newValue !== value) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setValueInstantly(newValue);
      }, delay);
    }
  };

  useEffect(() => {
    return clearTimeout(timeoutRef.current);
  }, []);

  return [value, setValue, setValueInstantly] as [T, typeof setValue, Dispatch<SetStateAction<T>>];
}

export function useValue<T>({
  defaultValue,
  onChange: suppliedOnChange,
  onChangeDebounce,
}: {
  defaultValue: T;
  onChange(newValue: T): void;
  onChangeDebounce?: number;
}) {
  const [value, setValue] = useState<T>(defaultValue);
  const debouncedValue = useDebouncedValue(value, onChangeDebounce);

  const onChangeCallback = useMemo(() => {
    if (onChangeDebounce) {
      return debounce(suppliedOnChange, onChangeDebounce);
    }
    return suppliedOnChange;
  }, [suppliedOnChange, onChangeDebounce]);

  const onChange = useCallback(
    (newValue: T) => {
      setValue(newValue);
      onChangeCallback(newValue);
    },
    [onChangeCallback]
  );

  const reset = useCallback(() => {
    setValue(defaultValue);
    onChangeCallback(defaultValue);
  }, [defaultValue, onChangeCallback]);

  return {
    value,
    debouncedValue,
    onChange,
    reset,
  };
}

interface Size {
  width: number;
  height: number;
}

export function useContainerSize<T extends HTMLElement = HTMLDivElement>(
  containerRef: React.RefObject<T>
): Size {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  useEffect(() => {
    function handleResize() {
      setSize({
        width: containerRef.current?.clientWidth || window.innerWidth,
        height: containerRef.current?.clientHeight || window.innerHeight,
      });
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    if (containerRef.current) {
      const currentRef = containerRef.current;
      currentRef.addEventListener('resize', handleResize);
      return () => {
        currentRef.removeEventListener('resize', handleResize);
        window.removeEventListener('resize', handleResize);
      };
    } else {
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [containerRef]);

  return size;
}

export const useBooleanState = (defaultValue = false) => {
  const [value, setValue] = useState(defaultValue);

  const toggle = () => setValue((old) => !old);

  return { value, setValue, toggle };
};

export const useControllableValue = <T,>(
  defaultValue: T,
  onChange: (newValue: T) => unknown,
  value?: T
) => {
  const [localValue, setLocalValue] = useState(value ?? defaultValue);

  const updateValue = useEvent((newValue: T) => {
    onChange(newValue);
    setLocalValue(newValue);
  });

  return [value ?? localValue, updateValue] as const;
};

export const hasChildOfType = (children: ReactNode, componentType: ComponentType<any>): boolean => {
  return Children.toArray(children).some(
    (child) => isValidElement(child) && child.type === componentType
  );
};

export const getFirstChildOfType = (
  children: ReactNode,
  type: ComponentType<any>
): ReactNode | undefined => {
  const matching = Children.toArray(children).filter(
    (child) => isValidElement(child) && child.type === type
  );
  if (matching.length > 0) {
    return matching[0];
  }
  return undefined;
};
